import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useTerminalSettings } from "./terminal-settings";
import {
  browserSocket,
  browserTimers,
  createTigerLink,
  type LinkCommand,
  type LinkOutcome,
  type LinkState,
} from "./tiger-link";

/**
 * Одно соединение с терминалом на вкладку (design.md D4): состояние видно в
 * подсказке кнопки любой строки, а не заводится заново на каждую. Объект живёт на уровне
 * модуля и не уничтожается при размонтировании компонентов — закрывает его
 * только выключение терминала в настройках.
 */
const tigerLink = createTigerLink({ createSocket: browserSocket, timers: browserTimers });

// Горячая замена модуля создаёт новый объект, а прежний держал бы сокет и
// таймеры переподключения: закрываем его вместе с заменой.
import.meta.hot?.dispose(() => tigerLink.dispose());

/**
 * Состояние соединения и отправка команды. Настройки берутся из
 * `useTerminalSettings`; смена включения, адреса или порта сама
 * переоткрывает соединение (`configure` идемпотентна, поэтому вызов из
 * нескольких компонентов безвреден).
 */
export function useTigerLink(): {
  state: LinkState;
  send: (command: LinkCommand) => Promise<LinkOutcome>;
} {
  const [settings] = useTerminalSettings();

  // Выбран MetaScalp — соединения с сервером сигналов TigerTrade нет (§89 п. 1).
  useEffect(() => {
    tigerLink.configure({
      ...settings,
      enabled: settings.enabled && settings.terminal === "tigertrade",
    });
  }, [settings]);

  const state = useSyncExternalStore(tigerLink.subscribe, tigerLink.getState, () => "off" as const);
  const send = useCallback((command: LinkCommand) => tigerLink.send(command), []);

  return { state, send };
}
